/**
 * Organization Settings - Custom Simulations Section
 * Manage custom simulations created by the organization
 */

import React, { useEffect, useState } from 'react';
import { Layers, Play, Archive, ArchiveRestore, MoreVertical, Clock, AlertCircle, RefreshCw, Edit, Trash2, Code, ExternalLink, Plus } from 'lucide-react';
import { useAuth } from '../../../contexts/AuthContext';
import { apiGet, apiPut, apiDelete } from '../../../utils/api';
import { useNavigate } from 'react-router-dom';

interface CustomSimulation {
    id: string;
    simulation_id: string;
    title: string;
    description: string;
    role: string;
    difficulty: string;
    duration: string;
    is_active: boolean;
    is_global?: boolean;
    is_programming?: boolean;
    tags?: string[];
    created_by: string;
    created_at: string;
    updated_at: string;
    run_count?: number;
}

interface CustomSimulationsResponse {
    status: string;
    data: {
        simulations: CustomSimulation[];
        total: number;
        active: number;
    };
}

export function OrganizationSettingsSimulations() {
    const { canManageOrg, currentOrg } = useAuth();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [simulations, setSimulations] = useState<CustomSimulation[]>([]);
    const [stats, setStats] = useState({ total: 0, active: 0 });
    const [openMenuId, setOpenMenuId] = useState<string | null>(null);
    const [toast, setToast] = useState<{ type: 'success' | 'error'; message: string } | null>(null);
    const [deleteConfirm, setDeleteConfirm] = useState<CustomSimulation | null>(null);
    const [isDeleting, setIsDeleting] = useState(false);

    const showToast = (type: 'success' | 'error', message: string) => {
        setToast({ type, message });
        setTimeout(() => setToast(null), 3000);
    };

    const fetchSimulations = async () => {
        try {
            setLoading(true);
            setError(null);

            const response = await apiGet<any>('/custom-simulations?include_inactive=true');

            if (response.status === 'success' && response.data) {
                // Handle both old key (custom_simulations) and new key (simulations)
                const rawSimulations = response.data.simulations || response.data.custom_simulations || [];
                
                // Filter to only show org's own custom simulations (not global ones)
                const orgSimulations = rawSimulations.filter((sim: CustomSimulation) => !sim.is_global);
                
                setSimulations(orgSimulations);
                
                // Calculate stats from filtered simulations
                const activeCount = orgSimulations.filter((sim: CustomSimulation) => sim.is_active).length;
                setStats({
                    total: orgSimulations.length,
                    active: activeCount
                });
            }
        } catch (err) {
            console.error('Failed to fetch custom simulations:', err);
            setError('Failed to load custom simulations');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        // Don't fetch if organization isn't loaded yet
        if (!currentOrg?.id) {
            return;
        }
        
        fetchSimulations();
    }, [currentOrg?.id]);

    const handleToggleActive = async (simulation: CustomSimulation) => {
        try {
            await apiPut(`/custom-simulations/${simulation.simulation_id}`, {
                is_active: !simulation.is_active
            });

            showToast('success', `${simulation.title} has been ${simulation.is_active ? 'archived' : 'restored'}.`);
            setOpenMenuId(null);
            fetchSimulations();
        } catch (err) {
            showToast('error', 'Failed to update simulation status');
        }
    };

    const handleEdit = (simulation: CustomSimulation) => {
        setOpenMenuId(null);
        navigate(`/app/studio?edit=${simulation.simulation_id}&readonly=false`);
    };

    const handleDelete = async (simulation: CustomSimulation) => {
        try {
            setIsDeleting(true);
            await apiDelete(`/custom-simulations/${simulation.simulation_id}`);
            showToast('success', `${simulation.title} has been deleted.`);
            setDeleteConfirm(null);
            fetchSimulations();
        } catch (err: any) {
            const errorMsg = err?.message || 'Failed to delete simulation';
            showToast('error', errorMsg);
        } finally {
            setIsDeleting(false);
        }
    };

    const handleCreateNew = () => {
        navigate('/app/studio');
    };

    const getDifficultyColor = (difficulty: string) => {
        switch (difficulty?.toLowerCase()) {
            case 'easy':
                return 'bg-emerald-100 text-emerald-700';
            case 'medium':
                return 'bg-yellow-100 text-yellow-700';
            case 'hard':
                return 'bg-red-100 text-red-700';
            default:
                return 'bg-slate-100 text-slate-700';
        }
    };

    const formatDate = (dateString: string) => {
        if (!dateString) return 'N/A';
        try {
            return new Date(dateString).toLocaleDateString('en-US', {
                year: 'numeric',
                month: 'short',
                day: 'numeric'
            });
        } catch {
            return 'N/A';
        }
    };

    if (loading) {
        return (
            <div className="space-y-6">
                <div className="h-8 w-64 bg-slate-200 rounded animate-pulse" />
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="h-24 bg-slate-200 rounded-xl animate-pulse" />
                    <div className="h-24 bg-slate-200 rounded-xl animate-pulse" />
                    <div className="h-24 bg-slate-200 rounded-xl animate-pulse" />
                </div>
                <div className="h-96 bg-slate-200 rounded-xl animate-pulse" />
            </div>
        );
    }

    return (
        <div className="space-y-6">
            {/* Toast */}
            {toast && (
                <div className={`fixed top-4 right-4 z-50 px-4 py-3 rounded-lg shadow-lg ${
                    toast.type === 'success' 
                        ? 'bg-emerald-50 border border-emerald-200 text-emerald-800' 
                        : 'bg-red-50 border border-red-200 text-red-800'
                }`}>
                    <p className="text-sm font-medium">{toast.message}</p>
                </div>
            )}

            {/* Header */}
            <div className="flex items-center justify-between">
                <div>
                    <h2 className="text-xl font-semibold text-slate-900">Custom Simulations</h2>
                    <p className="text-slate-500 text-sm mt-1">
                        View and manage custom simulations created by your team
                    </p>
                </div>
                <button
                    onClick={handleCreateNew}
                    className="flex items-center space-x-2 px-4 py-2 bg-emerald-500 hover:bg-emerald-600 text-white font-medium rounded-lg transition-colors"
                >
                    <Plus className="w-4 h-4" />
                    <span>Create New</span>
                </button>
            </div>

            {error && (
                <div className="bg-red-50 border border-red-200 rounded-xl p-4 flex items-center justify-between">
                    <div className="flex items-start space-x-3">
                        <AlertCircle className="w-5 h-5 text-red-600 mt-0.5" />
                        <p className="text-sm font-medium text-red-800">{error}</p>
                    </div>
                    <button
                        onClick={fetchSimulations}
                        className="px-3 py-1.5 text-sm font-medium text-red-700 hover:bg-red-100 rounded-lg transition-colors flex items-center space-x-1"
                    >
                        <RefreshCw className="w-4 h-4" />
                        <span>Retry</span>
                    </button>
                </div>
            )}

            {/* Stats Cards */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-5">
                    <div className="flex items-center space-x-2 text-slate-600 mb-2">
                        <Layers className="w-4 h-4 text-emerald-500" />
                        <span className="text-sm font-medium">Total Simulations</span>
                    </div>
                    <p className="text-2xl font-bold text-slate-900">{stats.total}</p>
                    <p className="text-xs text-slate-500 mt-1">Created by your team</p>
                </div>

                <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-5">
                    <div className="flex items-center space-x-2 text-slate-600 mb-2">
                        <Play className="w-4 h-4 text-emerald-500" />
                        <span className="text-sm font-medium">Active</span>
                    </div>
                    <p className="text-2xl font-bold text-slate-900">{stats.active}</p>
                    <p className="text-xs text-slate-500 mt-1">Available for use</p>
                </div>

                <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-5">
                    <div className="flex items-center space-x-2 text-slate-600 mb-2">
                        <Archive className="w-4 h-4 text-slate-400" />
                        <span className="text-sm font-medium">Archived</span>
                    </div>
                    <p className="text-2xl font-bold text-slate-900">{stats.total - stats.active}</p>
                    <p className="text-xs text-slate-500 mt-1">Not in use</p>
                </div>
            </div>

            {/* Simulations Table */}
            <div className="bg-white rounded-xl border border-slate-200 shadow-sm">
                <div className="px-6 py-4 border-b border-slate-100">
                    <h3 className="text-base font-semibold text-slate-900">Custom Simulations</h3>
                </div>

                {simulations.length === 0 ? (
                    <div className="text-center py-16">
                        <Layers className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                        <p className="text-lg text-slate-500">No custom simulations yet</p>
                        <p className="text-sm text-slate-400 mt-2">
                            Custom simulations created by your team will appear here
                        </p>
                        <button
                            onClick={handleCreateNew}
                            className="mt-4 inline-flex items-center space-x-2 px-4 py-2 bg-emerald-500 hover:bg-emerald-600 text-white font-medium rounded-lg transition-colors"
                        >
                            <Plus className="w-4 h-4" />
                            <span>Create Simulation</span>
                        </button>
                    </div>
                ) : (
                    <div className="p-4 space-y-4">
                        {simulations.map((sim) => (
                            <div 
                                key={sim.id || sim.simulation_id}
                                className={`border rounded-lg p-4 transition-all hover:shadow-md ${
                                    sim.is_active 
                                        ? 'border-slate-200 bg-white' 
                                        : 'border-slate-200 bg-slate-50 opacity-70'
                                }`}
                            >
                                {/* Card Header */}
                                <div className="flex items-start justify-between gap-4">
                                    <div className="flex-1 min-w-0">
                                        <div className="flex items-center flex-wrap gap-2">
                                            <h4 className="font-semibold text-slate-900 text-base">{sim.title}</h4>
                                            <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${
                                                sim.is_active 
                                                    ? 'bg-emerald-100 text-emerald-700' 
                                                    : 'bg-slate-200 text-slate-600'
                                            }`}>
                                                {sim.is_active ? 'Active' : 'Archived'}
                                            </span>
                                            {sim.is_programming && (
                                                <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium bg-blue-100 text-blue-700 rounded-full">
                                                    <Code className="w-3 h-3" />
                                                    Code
                                                </span>
                                            )}
                                        </div>
                                        {sim.description && (
                                            <p className="text-sm text-slate-500 mt-1 line-clamp-2">{sim.description}</p>
                                        )}
                                    </div>
                                    
                                    {/* Actions Menu */}
                                    <div className="relative flex-shrink-0">
                                        <button
                                            onClick={() => setOpenMenuId(openMenuId === sim.simulation_id ? null : sim.simulation_id)}
                                            className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
                                        >
                                            <MoreVertical className="w-5 h-5 text-slate-500" />
                                        </button>
                                        {openMenuId === sim.simulation_id && (
                                            <>
                                                <div 
                                                    className="fixed inset-0 z-40" 
                                                    onClick={() => setOpenMenuId(null)}
                                                />
                                                <div className="absolute right-0 mt-1 w-48 bg-white rounded-lg shadow-lg border border-slate-200 py-1 z-50">
                                                    <button
                                                        onClick={() => handleEdit(sim)}
                                                        className="w-full text-left px-4 py-2 text-sm text-slate-700 hover:bg-slate-50 flex items-center space-x-2"
                                                    >
                                                        <Edit className="w-4 h-4" />
                                                        <span>Edit Simulation</span>
                                                    </button>
                                                    <button
                                                        onClick={() => {
                                                            setOpenMenuId(null);
                                                            window.open(`/app/simulation-library?launch=${sim.simulation_id}`, '_blank');
                                                        }}
                                                        className="w-full text-left px-4 py-2 text-sm text-slate-700 hover:bg-slate-50 flex items-center space-x-2"
                                                    >
                                                        <ExternalLink className="w-4 h-4" />
                                                        <span>Launch Simulation</span>
                                                    </button>
                                                    <hr className="my-1 border-slate-100" />
                                                    <button
                                                        onClick={() => handleToggleActive(sim)}
                                                        className="w-full text-left px-4 py-2 text-sm text-slate-700 hover:bg-slate-50 flex items-center space-x-2"
                                                    >
                                                        {sim.is_active ? (
                                                            <>
                                                                <Archive className="w-4 h-4" />
                                                                <span>Archive</span>
                                                            </>
                                                        ) : (
                                                            <>
                                                                <ArchiveRestore className="w-4 h-4" />
                                                                <span>Restore</span>
                                                            </>
                                                        )}
                                                    </button>
                                                    {canManageOrg && (
                                                        <button
                                                            onClick={() => {
                                                                setOpenMenuId(null);
                                                                setDeleteConfirm(sim);
                                                            }}
                                                            className="w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50 flex items-center space-x-2"
                                                        >
                                                            <Trash2 className="w-4 h-4" />
                                                            <span>Delete</span>
                                                        </button>
                                                    )}
                                                </div>
                                            </>
                                        )}
                                    </div>
                                </div>

                                {/* Card Details */}
                                <div className="flex flex-wrap items-center gap-x-4 gap-y-2 mt-3 pt-3 border-t border-slate-100">
                                    <div className="flex items-center gap-1.5 text-sm text-slate-600">
                                        <span className="text-slate-400">Role:</span>
                                        <span className="font-medium">{sim.role || 'Custom'}</span>
                                    </div>
                                    <div className="flex items-center gap-1.5">
                                        <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${getDifficultyColor(sim.difficulty)}`}>
                                            {sim.difficulty}
                                        </span>
                                    </div>
                                    <div className="flex items-center gap-1.5 text-sm text-slate-600">
                                        <Clock className="w-4 h-4 text-slate-400" />
                                        <span>{sim.duration}</span>
                                    </div>
                                    <div className="flex items-center gap-1.5 text-sm text-slate-500">
                                        <span>Created {formatDate(sim.created_at)}</span>
                                    </div>
                                </div>

                                {/* Tags */}
                                {sim.tags && sim.tags.length > 0 && (
                                    <div className="flex flex-wrap gap-1.5 mt-3">
                                        {sim.tags.map((tag, idx) => (
                                            <span key={idx} className="px-2 py-0.5 text-xs bg-slate-100 text-slate-600 rounded-full">
                                                {tag}
                                            </span>
                                        ))}
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                )}
            </div>

            {/* Delete Confirmation Modal */}
            {deleteConfirm && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
                    <div className="bg-white rounded-xl shadow-2xl w-full max-w-md mx-4 p-6">
                        <div className="flex items-center space-x-3 mb-4">
                            <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center">
                                <Trash2 className="w-5 h-5 text-red-600" />
                            </div>
                            <h3 className="text-lg font-semibold text-slate-900">Delete Simulation</h3>
                        </div>
                        <p className="text-slate-600 mb-6">
                            Are you sure you want to delete <span className="font-medium text-slate-900">"{deleteConfirm.title}"</span>? 
                            This action cannot be undone.
                        </p>
                        <div className="flex justify-end space-x-3">
                            <button
                                onClick={() => setDeleteConfirm(null)}
                                disabled={isDeleting}
                                className="px-4 py-2 text-slate-700 hover:bg-slate-100 rounded-lg transition-colors font-medium"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={() => handleDelete(deleteConfirm)}
                                disabled={isDeleting}
                                className="px-4 py-2 bg-red-500 hover:bg-red-600 text-white rounded-lg transition-colors font-medium flex items-center space-x-2 disabled:opacity-50"
                            >
                                {isDeleting ? (
                                    <>
                                        <RefreshCw className="w-4 h-4 animate-spin" />
                                        <span>Deleting...</span>
                                    </>
                                ) : (
                                    <>
                                        <Trash2 className="w-4 h-4" />
                                        <span>Delete</span>
                                    </>
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

export default OrganizationSettingsSimulations;
